import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TAB_SLUGS, type JoinLinkCreated, type JoinLinkView } from '@scoutaround/script-breakdown-shared';

import { api, ApiError } from '../../lib/api.js';
import { useRefusal } from '../../lib/permissions.js';
import { Callout } from '../Callout.js';
import { formatDay } from './roles.js';

/**
 * THE LINK ITSELF, KEPT FOR THIS PAGE LOAD ONLY.
 *
 * The server stores a link's token as a hash and hands it back exactly once, to
 * whoever generated it. It is kept here — in this tab's memory, keyed by the
 * link's id — so the producer can copy it again while the page is open; a reload
 * forgets it, and the panel then says how to share the link again (generate a
 * replacement). Never written to storage, never put in a query cache that could
 * outlive the page.
 */
const shownLinks = new Map<string, string>();

/** Test hook: a fresh page load. */
export function forgetShownJoinLinks(): void {
  shownLinks.clear();
}

type Confirm = 'replace' | 'revoke' | null;

/**
 * SHARE INVITE LINK — the second way into the invite panel (owner brief
 * "Shareable Project Invitation Links & Approval Workflow", 2026-11-02, §2).
 *
 * One link per production, valid fourteen days. Anyone holding it may ASK to
 * join; nobody gets in without the owner's approval, one person at a time.
 * Replacing or revoking the link stops new requests only: requests already made
 * stay, and nobody already on the team loses access.
 *
 * The same cells as email invitations: generating and replacing take
 * `TEAM_MANAGE` (and the plan's collaboration module), revoking takes
 * `INVITATION_REVOKE`. The server gates; this hints.
 *
 * ONE PRIMARY PER STATE: Generate when there is no link, Copy while it is shown,
 * the confirmation's own button while one is asked.
 */
export function JoinLinkPanel({ projectId, planAllowsInvites }: { projectId: string; planAllowsInvites: boolean }) {
  const queryClient = useQueryClient();
  const manageRefusal = useRefusal('TEAM_MANAGE');
  const revokeRefusal = useRefusal('INVITATION_REVOKE');
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [, rerender] = useState(0);
  const { pathname } = useLocation();
  const billingHref = pathname.replace(/\/[^/]+\/?$/, `/${TAB_SLUGS.BILLING}`);

  const link = useQuery({ queryKey: ['join-link', projectId], queryFn: () => api.joinLink(projectId) });

  const generate = useMutation({
    mutationFn: (replace: boolean) => api.generateJoinLink(projectId, replace),
    onSuccess: (created: JoinLinkCreated) => {
      shownLinks.set(created.link.id, created.url);
      queryClient.setQueryData<JoinLinkView>(['join-link', projectId], { link: created.link });
      setConfirm(null);
      setError(null);
      setCopied(false);
      setStatus(
        created.replaced
          ? 'A new link is ready. The previous one no longer lets anyone ask to join.'
          : 'Your invitation link is ready.',
      );
      rerender((n) => n + 1);
    },
    onError: (e: unknown) => {
      setError(refusalOf(e, 'The link could not be generated.'));
      void queryClient.invalidateQueries({ queryKey: ['join-link', projectId] });
    },
  });

  const revoke = useMutation({
    mutationFn: () => api.revokeJoinLink(projectId),
    onSuccess: async () => {
      setConfirm(null);
      setError(null);
      setStatus('The link was revoked. Requests already made are unchanged.');
      await queryClient.invalidateQueries({ queryKey: ['join-link', projectId] });
    },
    onError: async (e: unknown) => {
      setError(refusalOf(e, 'The link could not be revoked.'));
      await queryClient.invalidateQueries({ queryKey: ['join-link', projectId] });
    },
  });

  const current = link.data?.link ?? null;
  const active = current !== null && current.state === 'ACTIVE' ? current : null;
  const url = active === null ? null : (shownLinks.get(active.id) ?? null);
  const busy = generate.isPending || revoke.isPending;

  const copy = async () => {
    if (url === null) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setStatus('Link copied.');
    } catch {
      // No clipboard (an insecure origin, a refusal): select it for a manual copy.
      const field = document.getElementById('join-link-url') as HTMLInputElement | null;
      field?.select();
      setStatus('Press Ctrl+C (⌘C on a Mac) to copy the selected link.');
    }
  };

  return (
    <section className="team-link-panel" data-testid="join-link-panel" aria-labelledby="join-link-title">
      <h4 className="team-link-title" id="join-link-title">
        Invite your team with one link
      </h4>
      <p className="team-dialog-lede" data-testid="join-link-explainer">
        Anyone with this link can request to join your production. Every request requires approval before access is
        granted.
      </p>

      {!planAllowsInvites ? (
        <Callout
          tone="info"
          title="Invitation links are part of the collaboration module"
          testId="join-link-plan"
          action={
            <Link to={billingHref} className="ctl" data-testid="join-link-plan-link">
              See Plan &amp; billing
            </Link>
          }
        >
          It is included from Lite upwards. Everyone already on this production stays on it.
        </Callout>
      ) : link.isPending ? (
        <p className="team-quiet" data-testid="join-link-loading">
          Loading the link…
        </p>
      ) : link.isError ? (
        <Callout tone="danger" title="The link could not be loaded" testId="join-link-load-error">
          {refusalOf(link.error, 'Try again in a moment.')}
        </Callout>
      ) : confirm !== null ? (
        <ConfirmStep
          kind={confirm}
          busy={busy}
          onConfirm={() => {
            setError(null);
            if (confirm === 'replace') generate.mutate(true);
            else revoke.mutate();
          }}
          onCancel={() => {
            setConfirm(null);
          }}
        />
      ) : active === null ? (
        <div className="team-link-body">
          {current !== null && current.state === 'EXPIRED' ? (
            <p className="team-field-help" data-testid="join-link-expired">
              The last link expired on {formatDay(current.expiresAt)}. Generate a new one to keep inviting.
            </p>
          ) : null}
          <div className="team-dialog-actions">
            <button
              type="button"
              className="btn btn-primary"
              data-testid="join-link-generate"
              disabled={busy || manageRefusal !== null}
              title={manageRefusal ?? undefined}
              onClick={() => {
                setStatus(null);
                generate.mutate(false);
              }}
            >
              {generate.isPending ? 'Generating…' : 'Generate invitation link'}
            </button>
          </div>
          {manageRefusal === null ? null : (
            <p className="team-field-help" data-testid="join-link-refusal">
              {manageRefusal}
            </p>
          )}
        </div>
      ) : (
        <div className="team-link-body">
          {url !== null ? (
            <div className="team-field">
              <label className="flbl" htmlFor="join-link-url">
                Invitation link
              </label>
              <div className="team-link-copy">
                <input
                  id="join-link-url"
                  data-testid="join-link-url"
                  readOnly
                  value={url}
                  onFocus={(e) => {
                    e.currentTarget.select();
                  }}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  data-testid="join-link-copy"
                  onClick={() => {
                    void copy();
                  }}
                >
                  {copied ? 'Copied' : 'Copy link'}
                </button>
              </div>
            </div>
          ) : (
            <p className="team-field-help" data-testid="join-link-hidden-note">
              This production has an active invitation link. For security, a link is shown only once, when it is
              generated. To share it again, generate a replacement — the current link then stops working for new
              requests.
            </p>
          )}
          <p className="team-field-help" data-testid="join-link-expiry">
            Valid until {formatDay(active.expiresAt)}. Requests appear under Invitations, in Join requests; only the
            production’s owner approves them.
          </p>
          <div className="team-dialog-actions">
            <button
              type="button"
              className={url === null ? 'btn btn-primary' : 'ctl'}
              data-testid="join-link-replace"
              disabled={busy || manageRefusal !== null}
              title={manageRefusal ?? undefined}
              onClick={() => {
                setStatus(null);
                setConfirm('replace');
              }}
            >
              Generate replacement link
            </button>
            <button
              type="button"
              className="ctl"
              data-testid="join-link-revoke"
              disabled={busy || revokeRefusal !== null}
              title={revokeRefusal ?? undefined}
              onClick={() => {
                setStatus(null);
                setConfirm('revoke');
              }}
            >
              Revoke link
            </button>
          </div>
        </div>
      )}

      {error === null ? null : (
        <Callout tone="danger" title="Nothing changed" testId="join-link-error">
          {error}
        </Callout>
      )}

      <p className="team-link-status" role="status" aria-live="polite" data-testid="join-link-status">
        {status ?? ''}
      </p>
    </section>
  );
}

function ConfirmStep({
  kind,
  busy,
  onConfirm,
  onCancel,
}: {
  kind: 'replace' | 'revoke';
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="team-link-confirm" data-testid={`join-link-confirm-${kind}`}>
      <p className="team-dialog-lede">
        {kind === 'replace'
          ? 'Generate a replacement link? The current link stops working for new requests. Requests already made stay, and nobody already on the team loses access.'
          : 'Revoke this link? It stops working for new requests. Requests already made stay, and nobody already on the team loses access.'}
      </p>
      <div className="team-dialog-actions">
        <button
          type="button"
          className="btn btn-primary"
          data-testid={`join-link-confirm-${kind}-yes`}
          data-autofocus
          disabled={busy}
          onClick={onConfirm}
        >
          {busy ? 'Working…' : kind === 'replace' ? 'Generate replacement' : 'Revoke link'}
        </button>
        <button type="button" className="ctl" data-testid="join-link-cancel" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function refusalOf(e: unknown, fallback: string): string {
  if (e instanceof ApiError && e.code === 'FEATURE_NOT_IN_PLAN') {
    return 'Invitation links need the collaboration module — it is included from Lite upwards. See Plan & billing.';
  }
  return e instanceof ApiError ? e.message : fallback;
}
